import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'
/** API */
import ProductInstructionsApi from 'api/setting/default-products/product-instruction.api'

/** UTILS || SLICE */
import { submitModal } from 'app/slice/modal.slice'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Box, Grid, Typography } from '@mui/material'
import HeaderCard from 'component/CardCustom/HeaderCard'
import { notiError, notiSuccess } from 'component/notifications/notifications'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import MainButton from 'new-components/buttons/main-button'
import SwitchButton from 'new-components/buttons/switch-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import AlertModal from 'new-components/modal/alert-modal'
import PageModal from 'new-components/modal/page-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

const initStateErrorMessage = {
  PRODUCT_INSTRUCTION_NAME: ``,
  IS_DUPLICATE_PRODUCT_INSTRUCTION_NAME: ``,
  PRODUCT_INSTRUCTION_NAME_STRING_BASE: ``,
  PRODUCT_INSTRUCTION_NAME_STRING_EMPTY: ``,
  PRODUCT_INSTRUCTION_NAME_MAXIMUM_LENGTH: ``,
  PRODUCT_INSTRUCTION_NAME_ANY_REQUIRED: ``,

  PRODUCT_INSTRUCTION_NAME_EN: ``,
  IS_DUPLICATE_PRODUCT_INSTRUCTION_NAME_EN: ``,
  PRODUCT_INSTRUCTION_NAME_EN_STRING_BASE: ``,
  PRODUCT_INSTRUCTION_NAME_EN_STRING_EMPTY: ``,
  PRODUCT_INSTRUCTION_NAME_EN_MAXIMUM_LENGTH: ``,
  PRODUCT_INSTRUCTION_NAME_EN_ANY_REQUIRED: ``
}
export default function ProductInstructions() {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [productInstructions, setProductInstructions] = useState([])

  const [productInstructionId, setProductInstructionId] = useState(Number)
  const [productInstructionName, setProductInstructionName] = useState('')
  const [productInstructionNameEn, setProductInstructionNameEn] = useState('')
  const [productInstructionStatus, setProductInstructionStatus] = useState('')

  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')
  const [disabledSubmit, setDisabledSubmit] = useState(false)
  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')
  const [isOpenPageModal, setIsOpenPageModal] = useState(false)


  const loadData = async () => {
    try {
      const resp = await ProductInstructionsApi.findAll({
        group: 'PRODUCT',
        page: page,
        pageLimit: pageLimit,
        search: search,
        sortBy: sortBy,
        sortType: sortType,
        status: status === 'ALL' ? '' : status
      })
      if (resp.status === 200) {
        setRowCount(resp.headers['x-total'])
        setProductInstructions(resp.data)
      } else {
        setProductInstructions([])
      }
    } catch (error) {
      notiError('ERROR')
    }
  }

  useEffect(() => {
    loadData()
  }, [page, pageLimit, sortBy, sortType])

  const submit = () => {
    if (!productInstructionName) return setErrorMessage({ ...errorMessage, ...{ PRODUCT_INSTRUCTION_NAME: t('PRODUCT_INSTRUCTION.MESSAGE.PRODUCT_INSTRUCTION_NAME') } })
    dispatch(submitModal())
    setDisabledSubmit(true)
    ProductInstructionsApi.create({ productInstructionName, productInstructionNameEn, group: 'PRODUCT' })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          notiSuccess(t('PRODUCT_INSTRUCTION.MESSAGE.SUCCESS.CREATE'), '', null, null)
          resetForm()
          loadData()
          setIsOpenPageModal(false)
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`PRODUCT_INSTRUCTION.MESSAGE.${err.message}`) } })
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`PRODUCT_INSTRUCTION.MESSAGE.${err.message}`) } })
      }).finally(() => setDisabledSubmit(false))
  }

  const submitEdit = () => {
    if (!productInstructionName) return setErrorMessage({ ...errorMessage, ...{ PRODUCT_INSTRUCTION_NAME: t(`PRODUCT_INSTRUCTION.MESSAGE.PRODUCT_INSTRUCTION_NAME`) } })

    dispatch(submitModal())
    setDisabledSubmit(true)
    ProductInstructionsApi.update(productInstructionId, { productInstructionName, productInstructionNameEn, status: productInstructionStatus, group: 'PRODUCT' })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          notiSuccess(t(`PRODUCT_INSTRUCTION.MESSAGE.SUCCESS.UPDATE`), '', null, null)
          resetForm()
          loadData()
          setIsOpenPageModal(false)
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`PRODUCT_INSTRUCTION.MESSAGE.${err.message}`) } })
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`PRODUCT_INSTRUCTION.MESSAGE.${err.message}`) } })
      }).finally(() => setDisabledSubmit(false))
  }

  const clearErrorMessage = () => {
    setErrorMessage(initStateErrorMessage)
  }

  const resetForm = () => {
    setProductInstructionId(0)
    setProductInstructionName('')
    setProductInstructionNameEn('')
    setProductInstructionStatus('')
    clearErrorMessage()
    setIsOpenPageModal(false)
  }

  const onActive = () => {
    if (id) {
      ProductInstructionsApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('PRODUCT_INSTRUCTION.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('PRODUCT_INSTRUCTION.MESSAGE.ERROR'))
        }
      })
    }
  }
  const onDelete = () => {
    if (id) {
      ProductInstructionsApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('PRODUCT_INSTRUCTION.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('PRODUCT_INSTRUCTION.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onEdit = (data: any) => {
    const { productInstructionId: pdInstructionId, productInstructionName: pdInstructionName, productInstructionNameEn: pdInstructionNameEn, status: pdInstructionStatus } = data
    setProductInstructionId(pdInstructionId)
    setProductInstructionName(pdInstructionName)
    setProductInstructionNameEn(pdInstructionNameEn)
    setProductInstructionStatus(pdInstructionStatus)
    setIsOpenPageModal(true)
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }

  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }

  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'productInstructionName', disablePadding: false, label: t('PRODUCT_INSTRUCTION.TABLE.CELL.PRODUCT_INSTRUCTION_NAME') },
    { id: 'productInstructionNameEn', disablePadding: false, label: t('PRODUCT_INSTRUCTION.TABLE.CELL.PRODUCT_INSTRUCTION_NAME_EN') },
    { id: 'status', disablePadding: false, label: t('PRODUCT_INSTRUCTION.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('PRODUCT_INSTRUCTION.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { productInstructionId: pdInstructionId, productInstructionName: pdInstructionName, productInstructionNameEn: pdInstructionNameEn, updatedBy, updatedAt, status: pdInstructionStatus } = objData
    const statusBtnActive = pdInstructionStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = pdInstructionStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const objRenderData = {
      key: pdInstructionId,
      id: pdInstructionId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: pdInstructionName },
        { option: 'TEXT', align: 'left', label: pdInstructionNameEn },
        { option: 'STATUS', align: 'center', label: pdInstructionStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
        oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
      />
    )
  }

  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(obj.productInstructionName)
    setNameEn(obj.productInstructionNameEn)
  }

  return (
    <Box>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`PRODUCT_INSTRUCTION.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`PRODUCT_INSTRUCTION.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { productInstructionName: name, productInstructionNameEn: nameEn && `(${nameEn})`, status: t(`PRODUCT_INSTRUCTION.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <HeaderCard text={t('PRODUCT_INSTRUCTION.TITLE')} />
      <Box paddingX="24px" paddingTop="16px">
        <Grid container spacing="16px">
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <TextInput
              label={t('PRODUCT_INSTRUCTION.SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              onChange={(event) => {
                const value = event.target.value
                if (value) {
                  handleChangeStatus(value)
                }
              }}
              options={[
                { value: 'ALL', name: t('STATUS.ALL') },
                { value: 'ACTIVE', name: t('STATUS.ACTIVE') },
                { value: 'INACTIVE', name: t('STATUS.INACTIVE') }
              ]}
              label={t('STATUS.LABEL')}
              value={status}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} xl={8}>
            <Box display={'flex'} justifyContent={'space-between'}>
              <MainButton
                title={t('INPUT.SEARCH')}
                type='primary'
                variant='contained'
                style={{ width: '120px' }}
                onClick={() => {
                  setPage(1)
                  loadData()
                }} />

              <MainButton
                title={t('PRODUCT_INSTRUCTION.BUTTON.ADD')}
                disabled={permissions.isCreate.disabled}
                type='primary'
                variant='contained'
                onClick={() => setIsOpenPageModal(true)}
                startIcon={<AddCircleIcon />}
              />
            </Box>
          </Grid>
        </Grid>
        <Box paddingTop="24px">
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortType={sortType}
            sortBy={sortBy}
            onSort={onRequestSort}
            setPageLimit={handleChangeRowsPerPage}
            setPage={handleChangePage}
            rowCount={rowCount}
            headCells={headCells}
            rowsData={productInstructions.map((val, i) => {
              return renderData(val, i)
            })}
          />
        </Box>
        <PageModal
          isOpen={isOpenPageModal}
          title={productInstructionId ? t('PRODUCT_INSTRUCTION.TITLE_UPDATE') : t('PRODUCT_INSTRUCTION.TITLE_CREATE')}
          size='XS'
          handleClose={resetForm}
          onCancel={{ onClick: resetForm, title: t('PRODUCT_PRESERVE.BUTTON.CANCEL') }}
          onSubmit={{ onClick: productInstructionId ? submitEdit : submit, title: t('PRODUCT_PRESERVE.BUTTON.SAVE'), disabled: disabledSubmit }}
        >
          <Box display="flex" flexDirection="column" gap="24px">
            {productInstructionId ? <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography>{t('INPUT.LABEL_STATUS')}</Typography>
              <SwitchButton onChange={() => setProductInstructionStatus(productInstructionStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')} checked={productInstructionStatus === 'ACTIVE' ? true : false} />
            </Box> : ''}
            <TextInput
              onChange={(event) => {
                setProductInstructionName(event.target.value)
                clearErrorMessage()
              }}
              value={productInstructionName}
              label={t('PRODUCT_INSTRUCTION.INPUT.PRODUCT_INSTRUCTION_NAME')}
              helperText={
                errorMessage.PRODUCT_INSTRUCTION_NAME ||
                errorMessage.IS_DUPLICATE_PRODUCT_INSTRUCTION_NAME ||
                errorMessage.PRODUCT_INSTRUCTION_NAME_STRING_BASE ||
                errorMessage.PRODUCT_INSTRUCTION_NAME_STRING_EMPTY ||
                errorMessage.PRODUCT_INSTRUCTION_NAME_MAXIMUM_LENGTH ||
                errorMessage.PRODUCT_INSTRUCTION_NAME_ANY_REQUIRED
              }
              required
            />
            <TextInput
              onChange={(event) => {
                setProductInstructionNameEn(event.target.value)
                clearErrorMessage()
              }}
              value={productInstructionNameEn}
              helperText={
                errorMessage.PRODUCT_INSTRUCTION_NAME_EN ||
                errorMessage.IS_DUPLICATE_PRODUCT_INSTRUCTION_NAME_EN ||
                errorMessage.PRODUCT_INSTRUCTION_NAME_EN_STRING_BASE ||
                errorMessage.PRODUCT_INSTRUCTION_NAME_EN_STRING_EMPTY ||
                errorMessage.PRODUCT_INSTRUCTION_NAME_EN_MAXIMUM_LENGTH ||
                errorMessage.PRODUCT_INSTRUCTION_NAME_EN_ANY_REQUIRED
              }
              label={t('PRODUCT_INSTRUCTION.INPUT.PRODUCT_INSTRUCTION_NAME_EN')}
            />
          </Box>
        </PageModal>
      </Box>
    </Box>
  )
}
